import { defaultError } from '@qpr/client-library';
import api, { ApiRequest, ApiResult } from '../../../api/api-caller';
import initialState, { MapViewState } from '../map-state';

type Attributes = Record<string, Record<string, any>>;

export interface HierarchyRequest {
  containerId?: string;
  containerRelation?: string;
  relation?: string;
  connector?: string;
  elementtypes?: string[];
  attributes: Attributes;
  sortBy?: string[];
  recursive?: boolean;
  recursionCount?: number;
  criteria?: string;
}

interface CacheData {
  [key: string]: any;
}

const errorHandler = (apiResult: ApiResult): MapViewState => {
  return {
    ...initialState,
    error: apiResult.error || defaultError
  };
};

const getRequestAttributes = (request: HierarchyRequest) => {
  const hasName =
    Object.keys(request.attributes).filter(attribute => attribute === 'name').length > 0;
  if (!hasName) return Object.keys({ ...request.attributes, name: { displayName: 'name' } });

  return Object.keys(request.attributes);
};

const getHierarchyFromAPI = async (request: HierarchyRequest): Promise<ApiResult> => {
  const hierarchyQuery: ApiRequest = {
    body: { ...request, attributes: getRequestAttributes(request) },
    method: 'POST'
  };
  const result: ApiResult = await api.asyncCallApi('hierarchy', hierarchyQuery);
  return result;
};

const viewModelCache: CacheData = {};

const createViewModel = (req: HierarchyRequest) => {
  let hierarchyResponse: ApiResult;

  const addAttributes = (attributes: Record<string, string>) => {
    const attributesWithDisplayNames = {} as Record<string, any>;
    Object.entries(attributes).forEach(([key, value]) => {
      if (req.attributes[key]) {
        attributesWithDisplayNames[key] = {
          value,
          displayName: (req.attributes && req.attributes[key].displayName) || key
        };
      }
    });

    return attributesWithDisplayNames;
  };

  if (!req.elementtypes) {
    req.elementtypes = ['Business Process'];
  }

  const fetchHierarchy = async (): Promise<ApiResult> => {
    const cacheKey: string = JSON.stringify(req) || '';

    if (viewModelCache[cacheKey]) {
      hierarchyResponse = viewModelCache[cacheKey];
    } else {
      hierarchyResponse = await getHierarchyFromAPI(req);

      viewModelCache[cacheKey] = hierarchyResponse;
    }
    return hierarchyResponse;
  };

  const transformData = ({
    id,
    name,
    typename,
    fullid,
    symbol,
    children,
    parentid,
    roles,
    typegroupname,
    ...rest
  }: any) => ({
    children: children.map(transformData),
    id,
    name,
    typename,
    fullid,
    symbol,
    parentid,
    roles,
    typegroupname,
    attributes: addAttributes({
      ...rest,
      id,
      typename,
      fullid,
      symbol,
      parentid,
      roles,
      typegroupname
    })
  });

  const getData = async (): Promise<MapViewState> => {
    if (!hierarchyResponse) {
      try {
        hierarchyResponse = await fetchHierarchy();
        if (!hierarchyResponse.ok) return errorHandler(hierarchyResponse);

        if (!hierarchyResponse.data[0]?.name)
          return {
            ...initialState,
            error: {
              message: 'No data to map.',
              details: `Element with id ${req.containerId} was not found. Check the element id given in URL.`
            }
          };
      } catch (error) {
        return {
          ...initialState,
          error: { message: 'Something went wrong', details: JSON.stringify(error) }
        };
      }
    }

    return {
      ...initialState,
      hierarchy: transformData(hierarchyResponse.data[0])
    };
  };

  return { getData };
};

export default createViewModel;
